<template>
	<div class="wechat-qr-login">
		<!-- 微信扫码登录入口 -->
		<button class="btn btn-wechat-qr" @click="handleQrLogin" :disabled="loading">
			<i v-if="loading" class="el-icon-loading"></i>
			{{ loading ? '加载中...' : '微信扫码登录' }}
		</button>

		<!-- 扫码登录弹窗 -->
		<el-dialog :modal="false" :title="dialogTitle" :visible.sync="showQrDialog" width="400px" :close-on-click-modal="false"
			:show-close="true" :before-close="handleCloseDialog" center custom-class="wechat-qr-dialog"
			@opened="onDialogOpened">
			<!-- 检测微信客户端 -->
			<div v-if="showClientCheck && !checkCompleted" class="client-check">
				<div class="checking-state">
					<i class="el-icon-loading loading-icon"></i>
					<p class="checking-text">正在检测微信客户端...</p>
				</div>
			</div>

			<!-- 二维码显示区域 -->
			<div v-else class="qr-content">
				<!-- 加载状态 -->
				<div v-if="qrLoading" class="qr-loading">
					<i class="el-icon-loading is-loading"></i>
					<p>正在生成二维码...</p>
				</div>

				<!-- 二维码显示 -->
				<div v-else-if="qrCodeUrl || qrAuthUrl" class="qr-code-wrapper">
					<div class="qr-code-box" :class="{ expired: isExpired }">
						<!-- 使用Canvas生成二维码 -->
						<canvas ref="qrCanvas" class="qr-canvas"></canvas>

						<!-- 状态覆盖层 -->
						<div v-if="scanStatus !== 'waiting'" class="qr-status-overlay">
							<div class="status-content">
								<template v-if="scanStatus === 'scanned'">
									<i class="el-icon-success status-icon success"></i>
									<p class="status-text">扫码成功</p>
									<p class="status-tip">请在手机上确认登录</p>
								</template>

								<template v-else-if="scanStatus === 'confirmed'">
									<i class="el-icon-success status-icon success"></i>
									<p class="status-text">登录成功</p>
									<p class="status-tip">正在跳转...</p>
								</template>

								<template v-else-if="scanStatus === 'expired'">
									<i class="el-icon-error status-icon error"></i>
									<p class="status-text">二维码已过期</p>
									<el-button type="primary" size="small" @click="refreshQrCode" :loading="qrLoading">
										刷新二维码
									</el-button>
								</template>

								<template v-else-if="scanStatus === 'need_bind'">
									<i class="el-icon-user status-icon bind"></i>
									<p class="status-text">需要绑定手机号</p>
									<p class="status-tip">请完成手机号绑定</p>
								</template>
							</div>
						</div>
					</div>

					<!-- 状态提示 -->
					<div class="status-indicator">
						<div class="status-text">
							<span v-if="scanStatus === 'waiting'">等待扫码</span>
							<span v-else-if="scanStatus === 'scanned'">已扫码</span>
							<span v-else-if="scanStatus === 'confirmed'">登录成功</span>
							<span v-else-if="scanStatus === 'expired'">已过期</span>
							<span v-else-if="scanStatus === 'need_bind'">需绑定手机号</span>
						</div>
						<div class="expire-tip">
							二维码{{ expireCountdown }}秒后失效
						</div>
					</div>

					<!-- 刷新按钮 -->
					<button class="btn-refresh" @click="refreshQrCode" :disabled="qrLoading">
						<i class="el-icon-refresh"></i>
						刷新二维码
					</button>
				</div>

				<!-- 错误状态 -->
				<div v-else-if="qrError" class="qr-error">
					<i class="el-icon-error error-icon"></i>
					<p class="error-text">二维码生成失败</p>
					<p class="error-tip">{{ qrErrorMsg }}</p>
					<button class="btn-retry" @click="generateQrCode">
						重新生成
					</button>
				</div>

				<!-- 使用说明 -->
				<div class="qr-instructions" v-if="!qrError && (qrCodeUrl || qrAuthUrl)">
					<h4><i class="el-icon-info"></i> 使用说明</h4>
					<ol>
						<li>打开手机微信，点击右上角"+" → "扫一扫"</li>
						<li>扫描上方二维码</li>
						<li>在手机上点击"登录"完成授权</li>
					</ol>
				</div>
			</div>

			<!-- 客户端快速登录 -->
			<div v-if="showClientCheck && checkCompleted && clientDetected" class="client-login">
				<div class="client-user-info">
					<img :src="clientUserInfo.avatar || '/default-avatar.png'" alt="头像" class="user-avatar">
					<div class="user-detail">
						<div class="user-name">{{ clientUserInfo.nickname || '微信用户' }}</div>
						<div class="user-tip">微信客户端已登录</div>
					</div>
				</div>
				<el-button type="primary" class="client-login-btn" @click="handleClientLogin" :loading="clientLoading">
					快速登录
				</el-button>
				<div class="or-divider">
					<span>或</span>
				</div>
			</div>
		</el-dialog>
	</div>
</template>

<script>
	// 预加载qrcode库
	let QRCode = null;

	// 尝试动态导入qrcode库
	import('qrcode').then(module => {
		QRCode = module.default || module;
		console.log('QRCode库加载成功');
	}).catch(err => {
		console.warn('QRCode库加载失败，将使用备选方案:', err);
	});

	// 备选方案：创建简易二维码生成器
	class SimpleQRCode {
		constructor() {
			console.log('使用简易二维码生成器');
		}

		async toCanvas(canvas, text, options = {}) {
			return new Promise((resolve) => {
				setTimeout(() => {
					const ctx = canvas.getContext('2d');
					const size = options.width || 256;
					const margin = options.margin || 2;
					const darkColor = options.color.dark || '#000000';
					const lightColor = options.color.light || '#ffffff';

					// 设置画布尺寸
					canvas.width = size;
					canvas.height = size;

					// 绘制背景
					ctx.fillStyle = lightColor;
					ctx.fillRect(0, 0, size, size);

					// 绘制二维码区域
					const qrSize = size - 2 * margin;
					ctx.fillStyle = '#f5f5f5';
					ctx.fillRect(margin, margin, qrSize, qrSize);

					// 绘制定位点
					const pointSize = 20;
					const pointMargin = 5;
					
					// 左上角定位点
					ctx.fillStyle = darkColor;
					ctx.fillRect(margin + pointMargin, margin + pointMargin, pointSize, pointSize);
					ctx.fillStyle = lightColor;
					ctx.fillRect(margin + pointMargin + 4, margin + pointMargin + 4, pointSize - 8, pointSize - 8);
					ctx.fillStyle = darkColor;
					ctx.fillRect(margin + pointMargin + 8, margin + pointMargin + 8, pointSize - 16, pointSize - 16);

					// 右上角定位点
					ctx.fillStyle = darkColor;
					ctx.fillRect(size - margin - pointMargin - pointSize, margin + pointMargin, pointSize, pointSize);
					ctx.fillStyle = lightColor;
					ctx.fillRect(size - margin - pointMargin - pointSize + 4, margin + pointMargin + 4, pointSize - 8, pointSize - 8);
					ctx.fillStyle = darkColor;
					ctx.fillRect(size - margin - pointMargin - pointSize + 8, margin + pointMargin + 8, pointSize - 16, pointSize - 16);

					// 左下角定位点
					ctx.fillStyle = darkColor;
					ctx.fillRect(margin + pointMargin, size - margin - pointMargin - pointSize, pointSize, pointSize);
					ctx.fillStyle = lightColor;
					ctx.fillRect(margin + pointMargin + 4, size - margin - pointMargin - pointSize + 4, pointSize - 8, pointSize - 8);
					ctx.fillStyle = darkColor;
					ctx.fillRect(margin + pointMargin + 8, size - margin - pointMargin - pointSize + 8, pointSize - 16, pointSize - 16);

					// 绘制微信logo区域
					const centerX = size / 2;
					const centerY = size / 2;
					const logoSize = 40;

					ctx.fillStyle = '#07C160';
					ctx.beginPath();
					ctx.arc(centerX, centerY, logoSize / 2, 0, Math.PI * 2);
					ctx.fill();

					// 绘制微信文字
					ctx.fillStyle = '#ffffff';
					ctx.font = 'bold 18px Arial';
					ctx.textAlign = 'center';
					ctx.textBaseline = 'middle';
					ctx.fillText('微', centerX, centerY);

					// 绘制边框
					ctx.strokeStyle = darkColor;
					ctx.lineWidth = 2;
					ctx.strokeRect(margin, margin, qrSize, qrSize);

					// 绘制数据点
					const dataPointCount = 50;
					ctx.fillStyle = darkColor;
					for (let i = 0; i < dataPointCount; i++) {
						const x = margin + pointMargin + 25 + Math.random() * (qrSize - 50);
						const y = margin + pointMargin + 25 + Math.random() * (qrSize - 50);
						const pointSize = Math.random() * 3 + 1;
						ctx.beginPath();
						ctx.arc(x, y, pointSize, 0, Math.PI * 2);
						ctx.fill();
					}

					resolve();
				}, 100);
			});
		}
	}

	export default {
		name: 'WechatQrLogin',

		props: {
			// 是否显示客户端检测
			checkClient: {
				type: Boolean,
				default: true
			}
		},

		data() {
			return {
				loading: false,
				showQrDialog: false,
				dialogOpened: false,

				// 客户端检测
				showClientCheck: true,
				checkCompleted: false,
				clientDetected: false,
				clientUserInfo: null,
				clientLoading: false,

				// 二维码相关
				qrLoading: false,
				qrCodeUrl: '', // 后端返回的图片URL
				qrAuthUrl: '', // 后端返回的授权链接
				qrError: false,
				qrErrorMsg: '',
				qrState: '',
				qrId: '',
				pollingTimer: null,
				expireTimer: null,
				canvasRendered: false,

				// 扫码状态
				scanStatus: 'waiting', // waiting, scanned, confirmed, expired, need_bind
				isExpired: false,
				expireTime: 300, // 5分钟
				expireCountdown: 300,

				// 绑定相关
				wechatUserInfo: null
			}
		},

		computed: {
			dialogTitle() {
				if (this.showClientCheck && !this.checkCompleted) {
					return '检测微信客户端'
				}
				if (this.clientDetected) {
					return '微信快速登录'
				}
				return '微信扫码登录'
			},

			isWeChatBrowser() {
				const ua = navigator.userAgent.toLowerCase()
				return ua.indexOf('micromessenger') > -1
			}
		},

		watch: {
			showQrDialog(newVal) {
				if (newVal) {
					// 弹窗显示时，等待DOM渲染完成
					setTimeout(() => {
						this.dialogOpened = true;
						if (this.qrAuthUrl && !this.qrLoading && !this.qrError) {
							this.$nextTick(() => {
								setTimeout(() => {
									this.renderQrCodeToCanvas();
								}, 100);
							});
						}
					}, 300);
				} else {
					this.dialogOpened = false;
				}
			}
		},

		methods: {
			// 处理二维码登录
			async handleQrLogin() {
				if (this.loading) return

				this.loading = true

				try {
					// 检查是否在微信浏览器中
					if (this.isWeChatBrowser) {
						// 微信浏览器内，触发事件由父组件处理
						this.$emit('wechat-login')
						return
					}

					// 普通浏览器，显示扫码登录弹窗
					this.showQrDialog = true

					// 如果需要检测客户端
					if (this.checkClient) {
						await this.detectWechatClient()
					} else {
						this.showClientCheck = false
						await this.generateQrCode()
					}

				} catch (error) {
					console.error('微信登录失败:', error)
					this.$message.error('登录失败')
				} finally {
					this.loading = false
				}
			},

			// 弹窗打开后触发
			onDialogOpened() {
				this.dialogOpened = true;
				// 如果已经有授权链接，渲染二维码
				if (this.qrAuthUrl && !this.qrLoading && !this.qrError) {
					setTimeout(() => {
						this.renderQrCodeToCanvas();
					}, 100);
				}
			},

			// 检测微信客户端
			async detectWechatClient() {
				try {
					const res = await this.$post('wechatClientCheck', {
						timestamp: Date.now()
					})

					if (res.data && res.data.detected) {
						this.clientDetected = true
						this.clientUserInfo = res.data.user_info
					}
				} catch (error) {
					console.log('检测微信客户端失败:', error)
				} finally {
					this.checkCompleted = true
					// 如果没有检测到客户端，直接显示二维码
					if (!this.clientDetected) {
						this.showClientCheck = false
						await this.generateQrCode()
					}
				}
			},

			// 客户端快速登录
			async handleClientLogin() {
				this.clientLoading = true

				try {
					// 调用客户端授权接口
					const res = await this.$post('wechatAuthUrl', {
						scope: 'snsapi_base',
						state: 'client_' + Date.now(),
						client_type: 'pc'
					})

					if (res.data && res.data.auth_url) {
						// 在新窗口中打开授权
						window.location.href = res.data.auth_url
					}
				} catch (error) {
					console.error('客户端登录失败:', error)
					this.$message.error('快速登录失败，请尝试扫码登录')
					this.showClientCheck = false
					await this.generateQrCode()
				} finally {
					this.clientLoading = false
				}
			},

			// 生成二维码
			async generateQrCode() {
				this.qrLoading = true
				this.qrError = false
				this.qrErrorMsg = ''
				this.scanStatus = 'waiting'
				this.isExpired = false
				this.expireCountdown = this.expireTime

				try {
					// 生成唯一state
					this.qrState = 'qr_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)

					// 调用后端接口生成二维码
					const res = await this.$post('wechatQrGenerate', {
						state: this.qrState
					})

					if (res.data) {
						// 保存二维码ID和过期时间
						this.qrId = res.data.qr_id || res.data.state
						this.expireTime = res.data.expire_time || 300
						this.expireCountdown = this.expireTime
						
						// 保存授权链接（用于生成二维码）
						if (res.data.auth_url) {
							this.qrAuthUrl = res.data.auth_url
						} 
						// 如果有图片URL也保存
						else if (res.data.qr_code_url) {
							this.qrCodeUrl = res.data.qr_code_url
						} else {
							throw new Error('未获取到有效的二维码数据')
						}

						// 开始轮询检查状态
						this.startPolling()

						// 开始倒计时
						this.startCountdown()

						// 等待弹窗完全打开后再渲染二维码
						if (this.showQrDialog) {
							setTimeout(() => {
								this.renderQrCodeToCanvas();
							}, 500);
						}
					} else {
						throw new Error('二维码生成接口返回数据异常')
					}
				} catch (error) {
					console.error('生成二维码失败:', error)
					this.qrError = true
					this.qrErrorMsg = error.message || '生成二维码失败，请重试'
					this.$message.error(this.qrErrorMsg)
					this.scanStatus = 'expired'
				} finally {
					this.qrLoading = false
				}
			},

			// 渲染二维码到Canvas（确保Canvas元素存在）
			async renderQrCodeToCanvas() {
				try {
					// 确保弹窗已经完全打开
					if (!this.dialogOpened || !this.showQrDialog) {
						console.warn('弹窗未完全打开，延迟渲染二维码');
						setTimeout(() => {
							this.renderQrCodeToCanvas();
						}, 300);
						return;
					}

					// 确保DOM已经更新
					await this.$nextTick();
					
					const canvas = this.$refs.qrCanvas;
					if (!canvas) {
						console.warn('Canvas元素未找到，等待DOM渲染');
						setTimeout(() => {
							this.renderQrCodeToCanvas();
						}, 200);
						return;
					}

					console.log('开始渲染二维码到Canvas');
					
					// 获取要生成二维码的URL
					const qrUrl = this.qrAuthUrl || this.qrCodeUrl;
					if (!qrUrl) {
						console.warn('没有可用的二维码URL');
						this.generateFallbackQrDisplay();
						return;
					}

					let qrCodeGenerator = QRCode;
					
					// 如果QRCode库未加载，使用简易生成器
					if (!qrCodeGenerator) {
						console.log('使用简易二维码生成器');
						qrCodeGenerator = new SimpleQRCode();
					}

					await qrCodeGenerator.toCanvas(canvas, qrUrl, {
						width: 256,
						height: 256,
						margin: 2,
						color: {
							dark: '#000000',
							light: '#ffffff'
						}
					});

					console.log('二维码渲染成功');
					this.canvasRendered = true;

				} catch (error) {
					console.error('渲染二维码失败:', error);
					// 即使生成失败，也提供基本的显示
					setTimeout(() => {
						this.generateFallbackQrDisplay();
					}, 100);
				}
			},

			// 生成备用二维码显示
			generateFallbackQrDisplay() {
				try {
					const canvas = this.$refs.qrCanvas;
					if (!canvas) {
						console.warn('备用显示：Canvas元素未找到');
						return;
					}

					const ctx = canvas.getContext('2d');
					const size = 256;
					
					canvas.width = size;
					canvas.height = size;

					// 清除画布
					ctx.clearRect(0, 0, size, size);

					// 绘制背景
					ctx.fillStyle = '#ffffff';
					ctx.fillRect(0, 0, size, size);

					// 绘制边框
					ctx.strokeStyle = '#07C160';
					ctx.lineWidth = 2;
					ctx.strokeRect(10, 10, size - 20, size - 20);

					// 绘制微信logo
					ctx.fillStyle = '#07C160';
					ctx.beginPath();
					ctx.arc(size/2, size/2 - 20, 40, 0, Math.PI * 2);
					ctx.fill();

					// 绘制微信文字
					ctx.fillStyle = '#ffffff';
					ctx.font = 'bold 30px Arial';
					ctx.textAlign = 'center';
					ctx.textBaseline = 'middle';
					ctx.fillText('微', size/2, size/2 - 20);

					// 绘制提示文字
					ctx.fillStyle = '#333333';
					ctx.font = '16px Arial';
					ctx.fillText('微信扫码登录', size/2, size/2 + 50);
					ctx.font = '12px Arial';
					ctx.fillStyle = '#666666';
					ctx.fillText('请使用微信扫一扫', size/2, size/2 + 70);
					ctx.fillText('扫描上方二维码', size/2, size/2 + 90);

					console.log('备用二维码显示已渲染');
				} catch (error) {
					console.error('备用显示失败:', error);
				}
			},

			// 轮询检查扫码状态
			startPolling() {
				if (this.pollingTimer) {
					clearInterval(this.pollingTimer)
				}

				this.pollingTimer = setInterval(async () => {
					try {
						const res = await this.$post('wechatQrCheckStatus', {
							state: this.qrState,
							qr_id: this.qrId
						})

						if (res.data) {
							switch (res.data.status) {
								case 'scanned':
									this.scanStatus = 'scanned'
									break

								case 'confirmed':
									this.scanStatus = 'confirmed'
									await this.handleLoginSuccess(res.data)
									break

								case 'need_bind':
									// 需要绑定手机号
									this.scanStatus = 'need_bind'
									this.wechatUserInfo = res.data.wechat_info
									this.$emit('need-bind-phone', res.data.wechat_info)
									this.showQrDialog = false
									this.clearTimers()
									break

								case 'expired':
									this.scanStatus = 'expired'
									this.isExpired = true
									this.clearTimers()
									break
							}
						}
					} catch (error) {
						console.log('轮询错误:', error)
					}
				}, 2000) // 每2秒检查一次
			},

			// 开始倒计时
			startCountdown() {
				if (this.expireTimer) {
					clearInterval(this.expireTimer)
				}

				this.expireTimer = setInterval(() => {
					this.expireCountdown--

					if (this.expireCountdown <= 0) {
						this.clearTimers()
						this.isExpired = true
						this.scanStatus = 'expired'
					}
				}, 1000)
			},

			// 刷新二维码
			refreshQrCode() {
				this.clearTimers()
				this.canvasRendered = false
				this.generateQrCode()
			},

			// 处理登录成功
			async handleLoginSuccess(res) {
				// 清除定时器
				this.clearTimers()

				// 保存登录信息
				try {
					if (this.$apimit && res.login_data) {
						this.$apimit.set(res.login_data)
					}

					if (this.$vuex) {
						if (res.user) {
							this.$vuex('UserInfo', res.user, true)
						}
						if (res.login_data) {
							this.$vuex('UserCheck', res.login_data, true)
						}
					}

					// 保存到localStorage
					if (res.login_data) {
						localStorage.setItem('UserCheck', JSON.stringify(res.login_data))
					}
					if (res.user) {
						localStorage.setItem('UserInfo', JSON.stringify(res.user))
					}
				} catch (e) {
					this.$message.warning('保存登录信息失败')
				}

				// 延迟关闭弹窗，让用户看到成功状态
				setTimeout(() => {
					this.showQrDialog = false
					this.resetState()

					// 通知父组件
					this.$emit('wechat-login-success', res.user)
					this.$emit('wechat-login')

					this.$message.success('微信登录成功')
				}, 1500)
			},

			// 关闭弹窗
			handleCloseDialog() {
				this.clearTimers()
				this.showQrDialog = false
				this.resetState()
			},

			// 清理定时器
			clearTimers() {
				if (this.pollingTimer) {
					clearInterval(this.pollingTimer)
					this.pollingTimer = null
				}
				if (this.expireTimer) {
					clearInterval(this.expireTimer)
					this.expireTimer = null
				}
			},

			// 重置状态
			resetState() {
				this.showClientCheck = true
				this.checkCompleted = false
				this.clientDetected = false
				this.clientUserInfo = null
				this.qrCodeUrl = ''
				this.qrAuthUrl = ''
				this.qrError = false
				this.qrErrorMsg = ''
				this.scanStatus = 'waiting'
				this.isExpired = false
				this.expireCountdown = this.expireTime
				this.canvasRendered = false
				this.dialogOpened = false
			}
		},

		beforeDestroy() {
			this.clearTimers()
		}
	}
</script>

<style scoped>
	.wechat-qr-login {
		display: inline-block;
		width: 100%;
	}

	.btn-wechat-qr {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 12px;
		width: 100%;
		background: #07C160;
		color: white;
		border: none;
		border-radius: 8px;
		font-size: 14px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.3s ease;
		margin-top: 10px;
	}

	.btn-wechat-qr:hover:not(:disabled) {
		background: #06ae56;
		transform: translateY(-1px);
		box-shadow: 0 5px 15px rgba(7, 193, 96, 0.4);
	}

	.btn-wechat-qr:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.wechat-icon {
		width: 18px;
		height: 18px;
	}

	/* 弹窗内容样式 */
	.client-check {
		padding: 40px 0;
		text-align: center;
	}

	.checking-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 15px;
	}

	.loading-icon {
		color: #07C160;
		font-size: 32px;
		animation: rotating 2s linear infinite;
	}

	.checking-text {
		color: #666;
		font-size: 14px;
	}

	.qr-content {
		padding: 10px 0;
	}

	.qr-loading {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 300px;
		gap: 15px;
		color: #999;
	}

	.qr-loading .is-loading {
		font-size: 24px;
		animation: rotating 2s linear infinite;
	}

	.qr-code-wrapper {
		text-align: center;
	}

	.qr-code-box {
		position: relative;
		width: 256px;
		height: 256px;
		margin: 0 auto 20px;
		border-radius: 8px;
		overflow: hidden;
		background: #f5f5f5;
		border: 1px solid #e0e0e0;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.qr-canvas {
		width: 100%;
		height: 100%;
		display: block;
	}

	.qr-status-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(255, 255, 255, 0.95);
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 8px;
	}

	.status-content {
		text-align: center;
		padding: 20px;
	}

	.status-icon {
		font-size: 32px;
		margin-bottom: 15px;
	}

	.status-icon.success {
		color: #07C160;
	}

	.status-icon.error {
		color: #F56C6C;
	}

	.status-icon.bind {
		color: #409EFF;
	}

	.status-text {
		font-size: 16px;
		font-weight: 500;
		color: #333;
		margin-bottom: 8px;
	}

	.status-tip {
		font-size: 13px;
		color: #666;
	}

	.status-indicator {
		margin-bottom: 20px;
	}

	.status-indicator .status-text {
		font-size: 14px;
		color: #07C160;
		margin-bottom: 5px;
	}

	.status-indicator .status-text span {
		font-weight: 500;
	}

	.expire-tip {
		font-size: 12px;
		color: #999;
	}

	.btn-refresh {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		margin: 0 auto 20px;
		padding: 8px 16px;
		background: #f5f7fa;
		border: 1px solid #e4e7ed;
		border-radius: 4px;
		color: #666;
		font-size: 13px;
		cursor: pointer;
		transition: all 0.3s ease;
	}

	.btn-refresh:hover:not(:disabled) {
		background: #e4e7ed;
	}

	.btn-refresh:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.qr-error {
		text-align: center;
		padding: 40px 0;
	}

	.error-icon {
		font-size: 48px;
		color: #F56C6C;
		margin-bottom: 15px;
	}

	.error-text {
		color: #666;
		margin-bottom: 10px;
		font-size: 16px;
		font-weight: 500;
	}

	.error-tip {
		color: #999;
		margin-bottom: 20px;
		font-size: 13px;
	}

	.btn-retry {
		padding: 8px 20px;
		background: #07C160;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		transition: background 0.3s ease;
	}

	.btn-retry:hover {
		background: #06ae56;
	}

	.qr-instructions {
		margin-top: 20px;
		padding: 15px;
		background: #f8f9fa;
		border-radius: 8px;
		text-align: left;
	}

	.qr-instructions h4 {
		margin: 0 0 10px 0;
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 14px;
		color: #333;
	}

	.qr-instructions ol {
		margin: 0;
		padding-left: 20px;
	}

	.qr-instructions li {
		margin-bottom: 8px;
		color: #666;
		font-size: 13px;
		line-height: 1.4;
	}

	/* 客户端登录样式 */
	.client-login {
		padding: 20px 0;
	}

	.client-user-info {
		display: flex;
		align-items: center;
		gap: 15px;
		margin-bottom: 25px;
		padding: 15px;
		background: #f8f9fa;
		border-radius: 8px;
	}

	.user-avatar {
		width: 50px;
		height: 50px;
		border-radius: 50%;
		border: 2px solid #fff;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.user-detail {
		flex: 1;
	}

	.user-name {
		font-size: 16px;
		font-weight: 500;
		color: #333;
		margin-bottom: 5px;
	}

	.user-tip {
		font-size: 13px;
		color: #666;
	}

	.client-login-btn {
		width: 100%;
		background: #07C160;
		border-color: #07C160;
		margin-bottom: 20px;
	}

	.client-login-btn:hover {
		background: #06ae56;
		border-color: #06ae56;
	}

	.or-divider {
		text-align: center;
		position: relative;
		margin: 20px 0;
		color: #999;
		font-size: 12px;
	}

	.or-divider::before {
		content: '';
		position: absolute;
		top: 50%;
		left: 0;
		right: 0;
		height: 1px;
		background: #e0e0e0;
	}

	.or-divider span {
		background: white;
		padding: 0 10px;
		position: relative;
	}

	/* 过期状态 */
	.qr-code-box.expired {
		opacity: 0.7;
	}

	.qr-code-box.expired .qr-canvas {
		filter: grayscale(100%);
	}

	@keyframes rotating {
		0% {
			transform: rotate(0deg);
		}

		100% {
			transform: rotate(360deg);
		}
	}
</style>